import type { ChangeEvent } from 'react';
import { useState } from 'react';
import { isQuizFileAnswer, quizFileHref, quizFileLabel, type QuizFileAnswer } from './quizFileAnswer';

const ACCEPT = '.jpg,.jpeg,.png,.gif,.webp,.pdf,.mp4,.webm,.mov,.mp3,.wav,.ogg,.m4a,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.zip';

function formatFileSize(size: QuizFileAnswer['size']): string | null {
  const bytes = typeof size === 'string' ? Number(size) : size;
  if (!bytes || !Number.isFinite(bytes) || bytes <= 0) return null;
  if (bytes < 1024) return `${Math.round(bytes)} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

interface QuizFileDropzoneProps {
  name: string;
  value?: unknown;
  disabled?: boolean;
  busy?: boolean;
  error?: string | null;
  readOnly?: boolean;
  onFile?: (file: File) => void;
  onClear?: () => void;
}

export function QuizFileDropzone({
  name,
  value,
  disabled = false,
  busy = false,
  error = null,
  readOnly = false,
  onFile,
  onClear,
}: QuizFileDropzoneProps) {
  const [localName, setLocalName] = useState<string | null>(null);
  const stored = isQuizFileAnswer(value) ? value : null;
  const label = quizFileLabel(stored) ?? localName;
  const href = quizFileHref(stored);
  const sizeLabel = formatFileSize(stored?.size);
  const locked = disabled || busy || readOnly;
  const canClear = !readOnly && (Boolean(onClear) || Boolean(localName));

  function handleChange(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || locked) return;
    if (onFile) {
      onFile(file);
      return;
    }
    setLocalName(file.name);
  }

  function clearFile() {
    setLocalName(null);
    onClear?.();
  }

  if (label) {
    return (
      <div className="lms-quiz-file">
        <div className="lms-quiz-file__card">
          <span className="material-symbols-outlined lms-quiz-file__icon" aria-hidden="true">
            draft
          </span>
          <div className="lms-quiz-file__name">
            {href ? (
              <a href={href} target="_blank" rel="noopener noreferrer">
                {label}
              </a>
            ) : (
              <strong>{label}</strong>
            )}
            {sizeLabel ? <span className="lms-quiz-file__meta">{sizeLabel}</span> : null}
          </div>
          {readOnly ? null : (
            <div className="lms-quiz-file__actions">
              <label className={`lms-quiz-file__change${locked ? ' lms-quiz-file__change--locked' : ''}`}>
                <input
                  type="file"
                  name={name}
                  accept={ACCEPT}
                  className="lms-quiz-file__input"
                  disabled={locked}
                  onChange={handleChange}
                />
                {busy ? 'Subiendo…' : 'Cambiar'}
              </label>
              {canClear ? (
                <button type="button" className="lms-quiz-file__remove" disabled={locked} onClick={clearFile}>
                  Quitar
                </button>
              ) : null}
            </div>
          )}
        </div>
        {error ? (
          <p className="lms-quiz-file__error" role="alert">
            {error}
          </p>
        ) : null}
      </div>
    );
  }

  if (readOnly) {
    return <p className="lms-quiz-review__text-answer">Sin archivo</p>;
  }

  return (
    <div className="lms-quiz-file">
      <label className={`lms-quiz-file__drop${locked ? ' lms-quiz-file__drop--locked' : ''}`}>
        <input
          type="file"
          name={name}
          accept={ACCEPT}
          className="lms-quiz-file__input"
          disabled={locked}
          onChange={handleChange}
        />
        <span className="material-symbols-outlined lms-quiz-file__icon" aria-hidden="true">
          upload_file
        </span>
        <p className="lms-quiz-file__title">{busy ? 'Subiendo archivo…' : 'Haz clic para subir un archivo'}</p>
        <p className="lms-quiz-file__hint">PDF, Office, imagen, audio, video o ZIP</p>
      </label>
      {error ? (
        <p className="lms-quiz-file__error" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}
